'use client';

import { useState } from 'react'
import { Terminal, Code, Cpu, ShieldAlert, CheckCircle } from 'lucide-react'

const codeExamples = {
  createLink: {
    curl: `curl -X POST https://lenklyst.com/api/external/v1/links \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://mybrand.com/promo-item",
    "slug": "blackfriday26",
    "password": "secretPassword",
    "iosDeepLink": "mybrand://promo/item",
    "androidDeepLink": "mybrand://promo/item"
  }'`,
    javascript: `fetch('https://lenklyst.com/api/external/v1/links', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    url: 'https://mybrand.com/promo-item',
    slug: 'blackfriday26',
    password: 'secretPassword',
    iosDeepLink: 'mybrand://promo/item',
    androidDeepLink: 'mybrand://promo/item'
  })
})
.then(res => res.json())
.then(data => console.log(data.shortUrl));`,
    python: `import requests

headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}

data = {
    'url': 'https://mybrand.com/promo-item',
    'slug': 'blackfriday26',
    'password': 'secretPassword',
    'iosDeepLink': 'mybrand://promo/item',
    'androidDeepLink': 'mybrand://promo/item'
}

response = requests.post(
    'https://lenklyst.com/api/external/v1/links',
    headers=headers,
    json=data
)

print(response.json()['shortUrl'])`
  },
  trackConversion: {
    curl: `curl -X POST https://lenklyst.com/api/external/v1/referrals/convert \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "referralCode": "alex",
    "event": "purchase",
    "value": 49.00,
    "currency": "USD"
  }'`,
    javascript: `fetch('https://lenklyst.com/api/external/v1/referrals/convert', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    referralCode: 'alex',
    event: 'purchase',
    value: 49.00,
    currency: 'USD'
  })
})
.then(res => res.json())
.then(data => console.log('Points awarded:', data.pointsAwarded));`,
    python: `import requests

headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}

data = {
    'referralCode': 'alex',
    'event': 'purchase',
    'value': 49.00,
    'currency': 'USD'
}

response = requests.post(
    'https://lenklyst.com/api/external/v1/referrals/convert',
    headers=headers,
    json=data
)

print('Success:', response.json()['success'])`
  }
}

export default function ApiDocsPage() {
  const [createLinkLang, setCreateLinkLang] = useState<'curl' | 'javascript' | 'python'>('curl')
  const [convertLang, setConvertLang] = useState<'curl' | 'javascript' | 'python'>('curl')

  return (
    <div className="space-y-12">
      
      {/* Title */}
      <div className="space-y-4">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
          <Terminal className="w-3.5 h-3.5" /> API Specification
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">
          Developer API Reference
        </h1>
        <p className="text-white/50 font-light text-sm leading-relaxed max-w-3xl">
          Integrate Lenklyst branding controls and referral metrics programmatically. Our endpoints accept JSON payloads and authenticate using standard Bearer keys.
        </p>
      </div>

      <hr className="border-white/5" />

      {/* Authentication */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
          <Cpu className="w-5 h-5 text-primary" /> 1. Authentication
        </h2>
        <p className="text-sm text-white/50 leading-relaxed font-light">
          Retrieve your API tokens in the developer dashboard under **Settings** &gt; **Developer Keys**. All requests must include the token in the headers as authorization:
        </p>
        <div className="p-4 bg-white/[0.02] border border-white/5 rounded-xl font-mono text-xs text-white/80">
          Authorization: Bearer <span className="text-primary">&lt;YOUR_API_KEY&gt;</span>
        </div>
      </div>

      {/* Endpoint: Create Link */}
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" /> 2. Shorten URL (Smart Link)
          </h2>
          <div className="flex items-center gap-3 mt-2">
            <span className="px-2.5 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-mono font-bold uppercase rounded-md">POST</span>
            <span className="font-mono text-sm text-white/80">/api/external/v1/links</span>
          </div>
        </div>

        <p className="text-sm text-white/50 leading-relaxed font-light">
          Create custom branded URLs programmatically. You can supply deep links for mobile redirecting, passwords for gates, and customize path aliases.
        </p>

        {/* Tab Selector */}
        <div className="space-y-3">
          <div className="flex border-b border-white/5">
            {(['curl', 'javascript', 'python'] as const).map(lang => (
              <button
                key={lang}
                onClick={() => setCreateLinkLang(lang)}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  createLinkLang === lang 
                    ? 'border-b-2 border-primary text-white' 
                    : 'text-white/40 hover:text-white'
                }`}
              >
                {lang === 'curl' ? 'cURL' : lang === 'javascript' ? 'JavaScript' : 'Python'}
              </button>
            ))}
          </div>
          <pre className="p-5 bg-black border border-white/5 rounded-2xl overflow-x-auto font-mono text-xs text-white/80 leading-relaxed">
            {codeExamples.createLink[createLinkLang]}
          </pre>
        </div>

        {/* Response */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-white/55">Response Example (200 OK)</h4>
          <pre className="p-5 bg-white/[0.01] border border-white/5 rounded-2xl overflow-x-auto font-mono text-xs text-green-400/90 leading-relaxed">
{`{
  "success": true,
  "shortUrl": "https://lenklyst.com/r/blackfriday26",
  "slug": "blackfriday26",
  "destinationUrl": "https://mybrand.com/promo-item"
}`}
          </pre>
        </div>
      </div>

      <hr className="border-white/5" />

      {/* Endpoint: Track Conversion */}
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" /> 3. Referral Conversion Event
          </h2>
          <div className="flex items-center gap-3 mt-2">
            <span className="px-2.5 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-mono font-bold uppercase rounded-md">POST</span>
            <span className="font-mono text-sm text-white/80">/api/external/v1/referrals/convert</span>
          </div>
        </div>

        <p className="text-sm text-white/50 leading-relaxed font-light">
          Submit conversions generated through referral campaigns. This rewards point values to advocates and fires webhooks.
        </p>

        {/* Tab Selector */}
        <div className="space-y-3">
          <div className="flex border-b border-white/5">
            {(['curl', 'javascript', 'python'] as const).map(lang => (
              <button
                key={lang}
                onClick={() => setConvertLang(lang)}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  convertLang === lang 
                    ? 'border-b-2 border-primary text-white' 
                    : 'text-white/40 hover:text-white'
                }`}
              >
                {lang === 'curl' ? 'cURL' : lang === 'javascript' ? 'JavaScript' : 'Python'}
              </button>
            ))}
          </div>
          <pre className="p-5 bg-black border border-white/5 rounded-2xl overflow-x-auto font-mono text-xs text-white/80 leading-relaxed">
            {codeExamples.trackConversion[convertLang]}
          </pre>
        </div>

        {/* Response */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-white/55">Response Example (200 OK)</h4>
          <pre className="p-5 bg-white/[0.01] border border-white/5 rounded-2xl overflow-x-auto font-mono text-xs text-green-400/90 leading-relaxed">
{`{
  "success": true,
  "pointsAwarded": 10,
  "advocate": "alex",
  "referralCode": "alex"
}`}
          </pre>
        </div>
      </div>

      <hr className="border-white/5" />

      {/* Error Codes */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-primary" /> Error Responses
        </h2>
        <div className="border border-white/5 rounded-2xl overflow-hidden bg-white/[0.01]">
          <table className="w-full text-left border-collapse text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-white/5 bg-white/5">
                <th className="p-4 font-bold uppercase text-white/60 tracking-wider">Status Code</th>
                <th className="p-4 font-bold uppercase text-white/60 tracking-wider">Error Message</th>
                <th className="p-4 font-bold uppercase text-white/60 tracking-wider">Resolution</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/5">
                <td className="p-4 font-mono font-bold text-red-400">401 Unauthorized</td>
                <td className="p-4 font-mono text-white/80">{"Invalid token or header structure"}</td>
                <td className="p-4 text-white/40 font-light">Supply bearer authentication keys retrieved in developer settings.</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="p-4 font-mono font-bold text-red-400">409 Conflict</td>
                <td className="p-4 font-mono text-white/80">{"Alias slug already in use"}</td>
                <td className="p-4 text-white/40 font-light">Supply a unique alias or slug, or let the ecosystem auto-generate one.</td>
              </tr>
              <tr>
                <td className="p-4 font-mono font-bold text-red-400">429 Rate Limited</td>
                <td className="p-4 font-mono text-white/80">{"Daily rate limit threshold reached"}</td>
                <td className="p-4 text-white/40 font-light">Upgrade your subscription tier to increase daily program requests.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Verification footer */}
      <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
        <p className="text-xs text-white/70 leading-relaxed font-light">
          Webhooks are fired instantly following program creations. Set up target webhooks inside the **Dashboard Integrations** tab.
        </p>
      </div>

    </div>
  )
}
